import { FC, useState, MouseEvent, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";

import Tooltip from "@mui/material/Tooltip";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";

import type { CourseCertificate } from "src/api/compliance";
import type { CourseStatus } from "src/components/CrewCourseOverviewTable";
import CourseRemainingDaysIcon from "src/components/CourseRemainingDaysIcon";
import CertificateActions from "src/components/Certificate/Actions";
import certificateStatusService from "src/services/certificateStatus";
import CurrentUserCrewContext from "src/context/CurrentUserCrewContext";
import CertificateActionsContext from "src/context/CertificateActions";
import { useTooltipPin } from "src/context/TooltipPinContext";
import useCrew from "src/hooks/useCrew";

interface CourseStatusTooltipProps {
  courseStatus: CourseStatus;
  userId: number;
  filterPurchased: boolean;
  onQuickPurchase?: () => void;
  onShowHistory?: (certificate: CourseCertificate) => void;
}

const CourseStatusTooltip: FC<CourseStatusTooltipProps> = ({
  courseStatus,
  userId,
  filterPurchased,
  onQuickPurchase,
  onShowHistory,
}) => {
  const { t } = useTranslation();
  const { crew } = useCrew();
  const { pinTooltip, unpinTooltip, isPinned: isTooltipPinned, hasAnyPinned } = useTooltipPin();
  const [open, setOpen] = useState(false);
  const tooltipId = useMemo(() => `${userId}-${courseStatus.course_id}`, [userId, courseStatus.course_id]);
  const isPinned = isTooltipPinned(tooltipId);

  // Create context value for Certificate Actions component
  const userCrewContextValue = {
    userId: userId,
    crewId: crew?.id || 0,
  };

  // Create context value for CertificateActions (showHistory, etc.)
  const certificateActionsValue = {
    showHistory: (certificate: CourseCertificate) => {
      setOpen(false);
      unpinTooltip();
      if (onShowHistory) {
        onShowHistory(certificate);
      }
    },
    getBoughtCourses: () => [],
  };

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    // If this tooltip is already pinned, unpin it
    if (isPinned) {
      unpinTooltip();
      setOpen(false);
    } else {
      // Close any other pinned tooltip and pin this one
      unpinTooltip(); // Close any existing pinned tooltip first
      pinTooltip(tooltipId);
      setOpen(true);
    }
  };

  const handleClickAway = () => {
    if (isPinned) {
      setOpen(false);
      unpinTooltip();
    }
  };

  // Close this tooltip if another one becomes pinned
  useEffect(() => {
    if (!isPinned && open) {
      setOpen(false);
    }
  }, [isPinned, open]);

  const tooltipContent = (
    <Box sx={{ position: "relative" }}>
      {/* Certificate Status Info */}
      <Box
        sx={{
          mb: 1.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "rgba(255, 255, 255, 0.7)",
            textTransform: "uppercase",
            fontSize: "0.7rem",
            fontWeight: 600,
            letterSpacing: "0.5px",
          }}
        >
          {t("course_tooltip.certificate_status")}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#fff",
            fontWeight: 500,
            mt: 0.5,
          }}
        >
          {certificateStatusService.getByCode(courseStatus.status)?.getDisplayName(t)}
        </Typography>
      </Box>

      {courseStatus.validUntil && (
        <Box
          sx={{
            mb: 1.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "rgba(255, 255, 255, 0.7)",
              textTransform: "uppercase",
              fontSize: "0.7rem",
              fontWeight: 600,
              letterSpacing: "0.5px",
            }}
          >
            {t("expire_date")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#fff",
              fontWeight: 500,
              mt: 0.5,
            }}
          >
            {courseStatus.validUntil}
          </Typography>
        </Box>
      )}

      {courseStatus.buffer && (
        <Box
          sx={{
            mb: 1.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "rgba(255, 255, 255, 0.7)",
              textTransform: "uppercase",
              fontSize: "0.7rem",
              fontWeight: 600,
              letterSpacing: "0.5px",
            }}
          >
            {t("buffer_zone")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#fff",
              fontWeight: 500,
              mt: 0.5,
            }}
          >
            {courseStatus.buffer}
          </Typography>
        </Box>
      )}

      {courseStatus.courseCertificate && (
        <>
          <Divider sx={{ my: 1, borderColor: "rgba(255, 255, 255, 0.2)" }} />
          {/* Certificate Actions */}
          <Box
            onClick={() => {
              setOpen(false);
              unpinTooltip();
            }}
            sx={{
              "& button": {
                color: "#fff",
                borderColor: "rgba(255, 255, 255, 0.3)",
                "&:hover": {
                  borderColor: "rgba(255, 255, 255, 0.5)",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              },
              "& svg": {
                color: "#fff",
              },
            }}
          >
            <CertificateActionsContext.Provider value={certificateActionsValue}>
              <CurrentUserCrewContext.Provider value={userCrewContextValue}>
                <CertificateActions courseCertificate={courseStatus.courseCertificate} />
              </CurrentUserCrewContext.Provider>
            </CertificateActionsContext.Provider>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <span style={{ display: "inline-block", padding: 5 }}>
        <Tooltip
          open={open}
          title={tooltipContent}
          placement="top"
          arrow
          disableFocusListener
          disableTouchListener
          slotProps={{
            popper: {
              disablePortal: false,
              sx: {
                "& .MuiTooltip-tooltip": {
                  backgroundColor: "rgba(0, 0, 0, 0.85)",
                  color: "#fff",
                  padding: 1.5,
                  maxWidth: 500,
                  borderRadius: "4px",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.4)",
                },
                "& .MuiTooltip-arrow": {
                  color: "rgba(0, 0, 0, 0.85)",
                },
              },
            },
          }}
        >
          <span style={{ display: "inline-block" }}>
            <span
              onClick={filterPurchased && onQuickPurchase ? undefined : handleClick}
              style={{
                position: "relative",
                display: "inline-block",
              }}
            >
              <CourseRemainingDaysIcon
                status={courseStatus.status}
                days={courseStatus.days}
                purchaseAction={filterPurchased && onQuickPurchase ? onQuickPurchase : undefined}
              />
            </span>
          </span>
        </Tooltip>
      </span>
    </ClickAwayListener>
  );
};

export default CourseStatusTooltip;
